import { cn } from '@/shared/helpers/classnames'
import { Checkbox } from '@/shared/ui/forms/inputs/checkbox'

import { type FC, useState } from 'react'
import { Check2, ChevronDown } from 'react-bootstrap-icons'
import { useTranslation } from 'react-i18next'

import {
  Content as CollapsibleContent,
  Root as CollapsibleRoot,
  Trigger as CollapsibleTrigger,
} from '@radix-ui/react-collapsible'

import { OptionLabel } from '../optionLabel'

import { useSearchContext } from './search/SearchContext'
import { useSelectionContext } from './selection/SelectionContext'

import { useSelectStateContext } from './SelectStateContext'
import type { SelectOption, SelectOptionProps } from './types'

/**
 * Hook to extract common option logic
 */
const useSelectOptionLogic = (option: SelectOption) => {
  const { selectedValues, handleSelect, handleSelectAll, selectAllState, handleGroupSelect } =
    useSelectionContext()

  const { mode } = useSelectStateContext()

  const isAll = option.type === 'all'
  const isGroup = option.type === 'group'
  const optionValue = option.value
  const isSelected = selectedValues.includes(optionValue)

  if (isAll) {
    return {
      isSelected: selectAllState.checked,
      handleClick: handleSelectAll,
      isIndeterminate: selectAllState.indeterminate,
    }
  }

  if (isGroup && option.children) {
    // Calculate group state based on its children
    const childValues = option.children.map(child => child.value)
    const selectedChildren = childValues.filter(v => selectedValues.includes(v))
    const allSelected = selectedChildren.length === childValues.length && childValues.length > 0
    const someSelected = selectedChildren.length > 0 && selectedChildren.length < childValues.length

    return {
      isSelected: allSelected,
      handleClick: () => handleGroupSelect?.(option),
      isIndeterminate: someSelected,
    }
  }

  const ariaProps = {
    'aria-disabled': option.disabled,
    'aria-selected': isSelected,
    tabIndex: option.disabled ? -1 : 0,
    role: mode === 'single' ? 'option' : 'menuitem',
  }

  const className = cn(
    'selectw-option',
    {
      'selectw-option-selected': isSelected,
      'selectw-option-disabled': option.disabled,
    },
    mode === 'group' && 'selectw-option-group-header',
  )

  // TODO : RadixDropdownContent prevents keyboard navigation. We need to use another lib to mange it correctly without redeveloping it (e.g react-a)

  return {
    ariaProps,
    className,
    isSelected,
    handleClick: () => handleSelect(optionValue),
    isIndeterminate: false,
  }
}

/**
 * SelectOptionSingle component - Option pour le mode single select
 *
 * @example
 * ```tsx
 * <SelectOptionSingle option={{ value: '1', label: 'Option 1' }} />
 * ```
 */
export const SelectOptionSingle: FC<SelectOptionProps> = ({ option }) => {
  const { isSelected, handleClick, ariaProps, className } = useSelectOptionLogic(option)

  return (
    <div {...ariaProps} className={className} onClick={option.disabled ? undefined : handleClick}>
      <div className="selectw-option-single">
        {isSelected && <Check2 className="selectw-option-check" />}
        <span
          className={cn('selectw-option-label', { 'selectw-option-label--selected': isSelected })}
        >
          {option.label}
        </span>
      </div>
    </div>
  )
}

SelectOptionSingle.displayName = 'SelectOptionSingle'

/**
 * SelectOptionMultiple component - Option pour le mode multi select
 *
 * @example
 * ```tsx
 * <SelectOptionMultiple option={{ value: '1', label: 'Option 1' }} />
 * ```
 */
export const SelectOptionMultiple: FC<SelectOptionProps> = ({ option }) => {
  const { size } = useSelectStateContext()
  const { isSelected, isIndeterminate, handleClick, ariaProps, className } =
    useSelectOptionLogic(option)

  return (
    <div {...ariaProps} className={className}>
      <div className="selectw-option-checkbox">
        <Checkbox.Option
          onChange={handleClick}
          checked={isSelected}
          disabled={option.disabled}
          indeterminate={isIndeterminate}
          size={size}
        >
          {option.label}
        </Checkbox.Option>
      </div>
    </div>
  )
}
SelectOptionMultiple.displayName = 'SelectOptionMultiple'

/**
 * SelectOptionGroup component - Option pour le mode group (groupe avec enfants)
 * Supporte le collapse/expand avec Radix UI Collapsible
 *
 * @example
 * ```tsx
 * <SelectOptionGroup option={{ value: 'group1', label: 'Groupe 1', type: 'group', children: [...] }} />
 * ```
 */
export const SelectOptionGroup: FC<SelectOptionProps> = ({ option }) => {
  const { size } = useSelectStateContext()
  const { searchValue } = useSearchContext()
  const { isSelected, isIndeterminate, handleClick, ariaProps, className } =
    useSelectOptionLogic(option)
  const [isOpen, setIsOpen] = useState(false)

  // If option is disabled, all sub-options are also disabled
  const effectiveDisabled = option.disabled

  // If search is in progress (not empty), collapsible is always open
  const isCollapsibleOpen = searchValue ? true : isOpen

  return (
    <CollapsibleRoot
      className="selectw-option-group"
      open={isCollapsibleOpen}
      onOpenChange={setIsOpen}
    >
      <div {...ariaProps} className={className}>
        <div className="selectw-option-checkbox">
          <Checkbox
            checked={isSelected}
            disabled={effectiveDisabled}
            indeterminate={isIndeterminate}
            size={size}
            onChange={e => {
              e.stopPropagation()
              handleClick()
            }}
          />
          <CollapsibleTrigger className="selectw-option-group-trigger">
            <OptionLabel size={size}>{option.label}</OptionLabel>
            <ChevronDown
              className={cn('selectw-option-group-chevron', {
                'selectw-option-group-chevron-open': isOpen,
              })}
            />
          </CollapsibleTrigger>
        </div>
      </div>
      {option.children && option.children.length > 0 && (
        <CollapsibleContent className="selectw-option-group-children">
          {option.children.map(child => (
            <SelectOptionMultiple
              key={child.value}
              option={{ ...child, disabled: effectiveDisabled || child.disabled }}
            />
          ))}
        </CollapsibleContent>
      )}
    </CollapsibleRoot>
  )
}
SelectOptionGroup.displayName = 'SelectOptionGroup'

export const SelectAllOption: FC = () => {
  const { t } = useTranslation()
  return (
    <SelectOptionMultiple
      option={{
        label: t('common.select.selectAll'),
        value: '',
        type: 'all',
      }}
    />
  )
}
SelectAllOption.displayName = 'SelectAllOption'
